import React, { useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box as Wrapper, Flex, Divider, Spinner, Text } from '@tonic-ui/react';
import { Alerts } from '../../components/alert';
import ResourcesLinks from './resources-links';
import BSPSupportAuthenticatedForm from './bsp-support-authenticated-form';
import { getC1Token, getAccountInfo } from '../../store';
import { useSendBSPSupportRequest } from '../../services/bsp-support-api';
import SuccessPage from './success-page';
import ErrorPage from './error-page';
import { useThemeColorStyle } from '../../hooks/use-theme-color-style';

const BSPSupportPage = (): JSX.Element => {
  const colorStyle = useThemeColorStyle();
  const { divider, backgroundColor } = colorStyle.support;
  const { t } = useTranslation();
  // For Error/Success screens

  const { c1Token } = useSelector(
    (state) => ({
      c1Token: getC1Token(state, 'token') as string,
    }),
    shallowEqual
  );

  const { userRegion } = useSelector(
    (state) => ({
      userRegion: (getAccountInfo(state, 'region')
        ? getAccountInfo(state, 'region')
        : 'us-1') as string,
    }),
    shallowEqual
  );

  const {
    mutateAsync: sendBSPSupportRequest,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useSendBSPSupportRequest(c1Token, userRegion);

  if (isLoading) {
    return (
      <Flex
        data-id="waiting"
        direction="row"
        align="center"
        justify="center"
        with="100%"
        height="100%"
      >
        <Text size="3xl" fontWeight="semibold" mr="2x">
          {t('bspSupport.supportPage.submissionWaiting')}
        </Text>
        <Spinner />
      </Flex>
    );
  }

  if (isSuccess) {
    return <SuccessPage id="bsp-support-form-success-page" />;
  }

  if (isError) {
    return <ErrorPage errorCode={error.status} />;
  }

  const handleSubmit = async (bspSupportRequestData: BSPSupportRequestData) => {
    await sendBSPSupportRequest(bspSupportRequestData);
  };

  return (
    <Flex direction="row" minHeight="100%" backgroundColor={backgroundColor}>
      <Flex flex="1" direction="column">
        <Wrapper mt="16px">
          <Alerts />
        </Wrapper>
        <BSPSupportAuthenticatedForm onSubmit={handleSubmit} />
      </Flex>
      <Divider orientation="vertical" borderColor={divider} width="2px" />
      <Flex height="100%" maxWidth="340px">
        <ResourcesLinks />
      </Flex>
    </Flex>
  );
};

BSPSupportPage.displayName = 'BSPSupportPage';
export default BSPSupportPage;
